import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Gift, Mail, Copy, Check, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReferralSection = () => {
  const { isAuthenticated } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchReferralData();
    }
  }, [isAuthenticated]);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/referral/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(response.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!referralData) return;
    try {
      await navigator.clipboard.writeText(referralData.referral_url);
      setCopied(true);
      toast.success('Link kopiert!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Fehler beim Kopieren');
    }
  };

  const shareViaEmail = () => {
    if (!referralData) return;
    const subject = encodeURIComponent('Probiere CarCortex-AI aus!');
    const body = encodeURIComponent(
      `Hey!\n\nIch nutze CarCortex-AI für KI-gestützte Fahrzeugdiagnosen und bin begeistert. Schau es dir mal an:\n\n${referralData.referral_url}\n\nDu kannst damit bis zu 87% Reparaturkosten sparen!\n\nViele Grüße`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaWhatsApp = () => {
    if (!referralData) return;
    const text = encodeURIComponent(
      `Hey! Ich nutze CarCortex-AI für KI-gestützte Fahrzeugdiagnosen und bin begeistert. Schau es dir mal an: ${referralData.referral_url} 🚗🔧`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaTelegram = () => {
    if (!referralData) return;
    const text = encodeURIComponent(
      `Hey! Ich nutze CarCortex-AI für KI-gestützte Fahrzeugdiagnosen. Schau es dir an: ${referralData.referral_url}`
    );
    window.open(`https://t.me/share/url?url=${referralData.referral_url}&text=${text}`, '_blank');
  };

  if (!isAuthenticated) {
    return (
      <section className="relative py-20 px-8" style={{ position: 'relative', zIndex: 10, isolation: 'isolate' }}>
        {/* Solid background to block parent overlays */}
        <div className="absolute inset-0" style={{ backgroundColor: '#0B0F14', zIndex: -1 }}></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-block p-4 rounded-full mb-6" style={{ backgroundColor: '#1F2937' }}>
            <Gift style={{ color: '#7CD4FD' }} size={48} />
          </div>
          <h2 className="text-4xl font-bold mb-4" style={{ color: '#FFFFFF', fontFamily: 'Manrope' }}>
            Empfehlen & €10 Guthaben erhalten
          </h2>
          <p className="text-xl mb-8" style={{ color: '#E6EDF3' }}>
            Lade Freunde ein und erhalte €10 Guthaben für jedes erfolgreiche Abonnement.
            Unbegrenzt! Deine Freunde sparen, du profitierst.
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
              <CardContent className="p-6">
                <div className="text-4xl font-bold mb-3" style={{ color: '#7CD4FD' }}>1</div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#FFFFFF' }}>Registrieren</h3>
                <p style={{ color: '#D1D5DB', fontSize: '1rem', lineHeight: '1.6' }}>
                  Erstelle ein Konto und erhalte deinen einzigartigen Empfehlungslink
                </p>
              </CardContent>
            </Card>
            <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
              <CardContent className="p-6">
                <div className="text-4xl font-bold mb-3" style={{ color: '#7CD4FD' }}>2</div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#FFFFFF' }}>Teilen</h3>
                <p style={{ color: '#D1D5DB', fontSize: '1rem', lineHeight: '1.6' }}>
                  Teile deinen Link mit Freunden per E-Mail, WhatsApp oder Social Media
                </p>
              </CardContent>
            </Card>
            <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
              <CardContent className="p-6">
                <div className="text-4xl font-bold mb-3" style={{ color: '#22C55E' }}>€10</div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#FFFFFF' }}>Verdienen</h3>
                <p style={{ color: '#D1D5DB', fontSize: '1rem', lineHeight: '1.6' }}>
                  Erhalte €10 Guthaben, sobald dein Freund ein Abonnement abschließt
                </p>
              </CardContent>
            </Card>
          </div>
          <div className="mt-10">
            <Button
              onClick={() => window.location.href = '/auth'}
              size="lg"
              className="px-8 py-6 text-lg font-semibold rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#7CD4FD', color: '#0B0F14' }}
            >
              Jetzt registrieren & Empfehlungslink erhalten
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (loading || !referralData) {
    return (
      <section className="py-20 px-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="max-w-4xl mx-auto text-center">
          <p style={{ color: '#94A3B8' }}>Lädt...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-20 px-8" style={{ position: 'relative', zIndex: 10, isolation: 'isolate' }}>
      {/* Solid background to block parent overlays */}
      <div className="absolute inset-0" style={{ backgroundColor: '#0B0F14', zIndex: -1 }}></div>
      <div className="max-w-5xl mx-auto relative">
        <div className="text-center mb-12">
          <div className="inline-block p-4 rounded-full mb-6" style={{ backgroundColor: '#1F2937' }}>
            <Gift style={{ color: '#7CD4FD' }} size={48} />
          </div>
          <h2 className="text-4xl font-bold mb-4" style={{ color: '#FFFFFF', fontFamily: 'Manrope' }}>
            Dein Empfehlungsprogramm
          </h2>
          <p className="text-xl" style={{ color: '#E6EDF3' }}>
            Teile CarCortex-AI und erhalte €10 pro erfolgreichem Abonnement
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
            <CardContent className="p-6 text-center">
              <div className="text-5xl font-bold mb-2" style={{ color: '#22C55E' }}>
                €{referralData.available_credits.toFixed(2)}
              </div>
              <div style={{ color: '#E6EDF3', fontSize: '1rem', fontWeight: '500' }}>Verfügbares Guthaben</div>
            </CardContent>
          </Card>
          <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
            <CardContent className="p-6 text-center">
              <div className="text-5xl font-bold mb-2" style={{ color: '#7CD4FD' }}>
                {referralData.total_paid_referrals}
              </div>
              <div style={{ color: '#E6EDF3', fontSize: '1rem', fontWeight: '500' }}>Erfolgreiche Empfehlungen</div>
            </CardContent>
          </Card>
          <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
            <CardContent className="p-6 text-center">
              <div className="text-5xl font-bold mb-2" style={{ color: '#A78BFA' }}>
                €{referralData.pending_credits.toFixed(2)}
              </div>
              <div style={{ color: '#E6EDF3', fontSize: '1rem', fontWeight: '500' }}>Ausstehend</div>
            </CardContent>
          </Card>
        </div>

        {/* Referral Link */}
        <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
          <CardContent className="p-8">
            <h3 className="text-2xl font-semibold mb-4" style={{ color: '#FFFFFF' }}>
              Dein Empfehlungslink
            </h3>
            <div className="flex gap-3 mb-6">
              <input
                type="text"
                value={referralData.referral_url}
                readOnly
                className="flex-1 px-4 py-3 rounded-lg text-base"
                style={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  color: '#E6EDF3'
                }}
              />
              <Button
                onClick={copyToClipboard}
                className="px-6"
                style={{
                  backgroundColor: copied ? '#22C55E' : '#7CD4FD',
                  color: '#0B0F14',
                  fontWeight: '600'
                }}
              >
                {copied ? <><Check size={20} /> Kopiert</> : <><Copy size={20} /> Kopieren</>}
              </Button>
            </div>

            {/* Share Buttons */}
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ color: '#FFFFFF' }}>
                <Share2 className="inline mr-2" size={20} />
                Jetzt teilen:
              </h4>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={shareViaEmail}
                  variant="outline"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#374151',
                    borderColor: '#4B5563',
                    color: '#FFFFFF'
                  }}
                >
                  <Mail size={20} />
                  Per E-Mail
                </Button>
                <Button
                  onClick={shareViaWhatsApp}
                  variant="outline"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#374151',
                    borderColor: '#4B5563',
                    color: '#FFFFFF'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </Button>
                <Button
                  onClick={shareViaTelegram}
                  variant="outline"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#374151',
                    borderColor: '#4B5563',
                    color: '#FFFFFF'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121L8.08 13.768l-2.972-.924c-.645-.203-.658-.645.136-.954l11.617-4.48c.536-.194 1.006.128.832.954z"/>
                  </svg>
                  Telegram
                </Button>
              </div>
            </div>

            {/* Info Box */}
            <div 
              className="mt-8 p-6 rounded-lg"
              style={{ backgroundColor: '#1F2937', borderLeft: '4px solid #7CD4FD' }}
            >
              <p style={{ color: '#FFFFFF', fontSize: '1.05rem', lineHeight: '1.7' }}>
                💡 <strong>So funktioniert's:</strong> Teile deinen Link mit Freunden. Wenn sie sich anmelden und ein Abonnement abschließen, erhältst du €10 Guthaben. Das Guthaben kannst du für deine Abonnements einlösen. Unbegrenzt!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
